import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import { mdiOpenInNew } from '@mdi/js';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import Badge from '@/components/layout/Badge';
import Skeleton from '@/components/layout/Skeleton';
import VBox from '@/components/layout/VBox';
import { formatarMoeda, UNIDADE_PADRAO } from '@tera/shared/src/types/Catalogo';
import { ORIGEM_LABEL, STATUS_BADGE, STATUS_LABEL, formatarData } from './constantes';
import styles from './page.module.css';

const DetalheOrcamento = ({ id }: { id: number }) => {
    const query = useQuery(['orcamento', id], async () => {
        const res = await axios.get(`/api/orcamento/${id}`);
        return res.data.result;
    });

    if (query.isLoading) {
        return (
            <VBox gap={2}>
                <Skeleton format="box" />
                <Skeleton format="box" />
            </VBox>
        );
    }

    const detalhe = query.data;
    if (!detalhe) return null;

    const linkPublico = detalhe.token
        ? `${window.location.origin}/orcamento/${detalhe.token}`
        : null;

    return (
        <div className={styles.detalhe}>
            <div className={styles.detalheGrid}>
                <div className={styles.detalheItem}>
                    <label>Status</label>
                    <Badge variant={STATUS_BADGE[detalhe.status] ?? 'secondary'}>
                        {STATUS_LABEL[detalhe.status] ?? detalhe.status}
                    </Badge>
                </div>
                <div className={styles.detalheItem}>
                    <label>Origem</label>
                    <span>{ORIGEM_LABEL[detalhe.origem] ?? detalhe.origem}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Catálogo</label>
                    <span>{detalhe.catalogo?.nome ?? '—'}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Cliente</label>
                    <span>{detalhe.cliente?.nome ?? '—'}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Canal</label>
                    <span>{detalhe.canal?.nome ?? '—'}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Cupom</label>
                    <span>{detalhe.cupom_codigo ?? '—'}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Emitido em</label>
                    <span>{formatarData(detalhe.enviado_em)}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Válido até</label>
                    <span>{formatarData(detalhe.valido_ate)}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Aceito em</label>
                    <span>{formatarData(detalhe.aceito_em)}</span>
                </div>
                <div className={styles.detalheItem}>
                    <label>Cancelado em</label>
                    <span>{formatarData(detalhe.cancelado_em)}</span>
                </div>
            </div>

            <table className={styles.tabelaItens}>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th className={styles.numero}>Qtd.</th>
                        <th className={styles.numero}>Unitário</th>
                        <th className={styles.numero}>Subtotal</th>
                    </tr>
                </thead>
                <tbody>
                    {(detalhe.itens ?? []).map((item: any) => (
                        <tr key={item.id}>
                            <td>{item.nome}</td>
                            <td className={styles.numero}>
                                {item.quantidade} {item.unidade || UNIDADE_PADRAO}
                            </td>
                            <td className={styles.numero}>
                                {formatarMoeda(item.preco_unitario, detalhe.moeda)}
                            </td>
                            <td className={styles.numero}>
                                {formatarMoeda(item.subtotal, detalhe.moeda)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className={styles.totais}>
                <div className={styles.linhaTotal}>
                    <span>Subtotal</span>
                    <span>{formatarMoeda(detalhe.subtotal, detalhe.moeda)}</span>
                </div>
                {detalhe.desconto_valor > 0 && (
                    <div className={styles.linhaTotal}>
                        <span>{detalhe.desconto_descricao ?? 'Desconto'}</span>
                        <span>-{formatarMoeda(detalhe.desconto_valor, detalhe.moeda)}</span>
                    </div>
                )}
                <div className={`${styles.linhaTotal} ${styles.totalFinal}`}>
                    <span>Total</span>
                    <span>{formatarMoeda(detalhe.total, detalhe.moeda)}</span>
                </div>
            </div>

            <HBox>
                <Botao
                    variant="outline-primary"
                    icon={mdiOpenInNew}
                    disabled={!linkPublico}
                    onClick={() =>
                        linkPublico && window.open(linkPublico, '_blank', 'noopener,noreferrer')
                    }
                >
                    Abrir link do cliente
                </Botao>
            </HBox>
        </div>
    );
};

export default DetalheOrcamento;
